import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { XpmLogo } from '../XpmLogo';

type XpmIntegrationStatus = 'not-connected' | 'connected' | 'error' | 'access-lost';

type XpmIntegrationWrapperProps = {
  status: XpmIntegrationStatus;
  connectedAt?: Date;
  /** Only for `access-lost`: true while the Xero window is open, so RECONNECT is disabled and shows a spinner. */
  isReconnecting?: boolean;
  onConnect?: () => void;
  onReconnect?: () => void;
  onDisconnect?: () => void;
  onHowItWorks?: () => void;
  onContactSupport?: () => void;
  onLearnMore?: () => void;
  howItWorksUrl?: string;
  learnMoreUrl?: string;
};

/**
 * Hardcoded values from Figma that don't have corresponding design tokens:
 * - Logo container padding: 10px (between spacing.xs=8px and spacing.sm=12px)
 * - Chip icon size: 12px (Figma: fontSize 12px for FiberManualRecord)
 * - Icon size: 24px (OpenInNew icon)
 * - Border width: 1px (logo container border)
 * - Not Connected chip width: 116px in Figma (currently auto-sized)
 * - Disconnect button height: 44px (Figma: 2.75rem)
 * - Reconnect spinner size: 20px (design board A2: 20px spinner, same as the OpenInNew button icon)
 */

// Approved copy, issue #2439 design comment, copy table row 1.
const ACCESS_LOST_STATUS_LINE =
  'VerifiMe has no access to your Xero Practice Manager. Select Reconnect, then approve VerifiMe in Xero. Your XPM clients and their VerifiMe fields stay the same.';

const formatConnectedDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${month}/${day}/${year} ${displayHours}:${minutes}${ampm}`;
};

type StatusChipConfig = {
  label: string;
  bgColor: string;
  textColor: string;
  iconColor: string;
};

const STATUS_CHIP_CONFIG: Record<XpmIntegrationStatus, StatusChipConfig> = {
  connected: {
    label: 'Active',
    // Figma: --secondary/g500 (#448533) → using status.success tokens
    bgColor: 'status.success.fill',
    textColor: 'status.success.text',
    iconColor: 'status.success.icon',
  },
  error: {
    label: 'Error',
    // Figma: --secondary/r500 (#c13635) → using status.error tokens
    bgColor: 'status.error.fill',
    textColor: 'status.error.text',
    iconColor: 'status.error.icon',
  },
  'access-lost': {
    label: 'No access',
    // Design System Y500 with black text and icon. `status.warning.fill` is wrong here: the
    // clarity light theme sets it to shade 600, which is not the Design System's Y500.
    bgColor: 'warning.main',
    textColor: 'common.black',
    iconColor: 'common.black',
  },
  'not-connected': {
    label: 'Not Connected',
    // Figma: --secondary/n500 (#6e7179) → using grey.500
    bgColor: 'grey.500',
    textColor: 'common.white',
    iconColor: 'common.white',
  },
};

// An unknown status (a caller outside the type) reads as Not Connected, as it always has.
const getStatusChipConfig = (status: XpmIntegrationStatus): StatusChipConfig =>
  STATUS_CHIP_CONFIG[status] ?? STATUS_CHIP_CONFIG['not-connected'];

type StatusDescriptionProps = { status: XpmIntegrationStatus };

/** The paragraph under the title: the pitch before connecting, the lost-access line without access. */
const StatusDescription = ({ status }: Readonly<StatusDescriptionProps>) => {
  if (status === 'not-connected') {
    return (
      <Typography variant="body1">
        Integrate with Xero Practice Manager (XPM) to request and receive
        real-time verifications of your customers.
      </Typography>
    );
  }
  if (status === 'access-lost') {
    return (
      <Typography
        variant="body1"
        // Board A1 sets this line at 1.5, the long line height, not the theme's 1.2 body1.
        sx={(theme) => ({ lineHeight: theme.semantic.lineHeight.long })}
      >
        {ACCESS_LOST_STATUS_LINE}
      </Typography>
    );
  }
  return null;
};

type AccessLostActionsProps = {
  isReconnecting?: boolean;
  onReconnect?: () => void;
  onHowItWorks: () => void;
};

/**
 * The No access card's buttons (boards A1 and A2): Reconnect is the only action that changes
 * anything. No Disconnect (it does not work during lost access) and no Contact Support.
 */
const AccessLostActions = ({
  isReconnecting = false,
  onReconnect,
  onHowItWorks,
}: Readonly<AccessLostActionsProps>) => (
  <>
    <Button
      variant="contained"
      onClick={onReconnect}
      data-action="reconnect-xpm"
      disabled={isReconnecting}
      aria-busy={isReconnecting || undefined}
      startIcon={isReconnecting && <CircularProgress size={20} color="inherit" />}
      sx={(theme) => ({
        // Board A2: the Design System's disabled fill with white text and spinner,
        // not MUI's default pale disabled button, which hides the spinner.
        '&.Mui-disabled': {
          backgroundColor: theme.palette.interactive.disabled.fill,
          color: theme.palette.interactive.disabled.text,
        },
      })}
    >
      RECONNECT
    </Button>
    <Button variant="outlined" onClick={onHowItWorks} endIcon={<OpenInNewIcon />}>
      HOW IT WORKS
    </Button>
  </>
);

const XpmIntegrationWrapperComponent = ({
  status,
  connectedAt,
  isReconnecting,
  onConnect,
  onReconnect,
  onDisconnect,
  onHowItWorks,
  onContactSupport,
  onLearnMore,
  howItWorksUrl,
  learnMoreUrl,
}: Readonly<XpmIntegrationWrapperProps>) => {
  const chipConfig = getStatusChipConfig(status);

  const handleLearnMore = () => {
    if (onLearnMore) {
      onLearnMore();
    } else if (learnMoreUrl) {
      window.open(learnMoreUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleHowItWorks = () => {
    if (onHowItWorks) {
      onHowItWorks();
    } else if (howItWorksUrl) {
      window.open(howItWorksUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Paper
      elevation={1}
      sx={(theme) => ({
        p: theme.semantic.spacing.lg,
        borderRadius: theme.semantic.borderRadius.sm,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.semantic.spacing.base,
      })}
    >
      {/* Header Row */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {/* Status Chip - Figma: 24px height, icon 16px container with 12px icon */}
        <Chip
          icon={<FiberManualRecordIcon sx={{ fontSize: 12 }} />}
          label={chipConfig.label}
          size="small"
          sx={(theme) => ({
            bgcolor: chipConfig.bgColor,
            color: chipConfig.textColor,
            fontWeight: theme.semantic.fontWeight.regular,
            fontSize: theme.semantic.fontSize.xs,
            px: theme.semantic.spacing.xs,
            py: theme.semantic.spacing['3xs'],
            '& .MuiChip-icon': {
              color: chipConfig.iconColor,
            },
          })}
        />

        {/* Learn More Link - Figma: 16px font, 24px icon */}
        <Link
          component="button"
          onClick={handleLearnMore}
          underline="always"
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: theme.semantic.spacing.xs,
            color: theme.palette.primary.main,
            fontSize: theme.semantic.fontSize.base,
            fontWeight: theme.semantic.fontWeight.regular,
            cursor: 'pointer',
          })}
        >
          Learn more about XPM
          {/* Hardcoded: 24px icon size (Figma: size-[24px]) */}
          <OpenInNewIcon sx={{ fontSize: 24 }} />
        </Link>
      </Stack>

      {/* Content Row */}
      <Stack
        direction="row"
        alignItems="center"
        sx={(theme) => ({
          gap: theme.semantic.spacing.lg,
        })}
      >
        {/* XPM Logo */}
        <Box
          sx={(theme) => ({
            backgroundColor: theme.palette.surface.logo,
            borderRadius: theme.semantic.borderRadius.sm,
            padding: '10px', // Hardcoded: between spacing.xs (8px) and spacing.sm (12px)
            border: `1px solid ${theme.palette.grey[100]}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          })}
        >
          <XpmLogo size="large" />
        </Box>

        {/* Content Wrapper */}
        <Stack
          sx={(theme) => ({
            gap: theme.semantic.spacing.base,
            flex: 1,
          })}
        >
          {/* Title */}
          <Typography variant="h4">
            {status === 'not-connected'
              ? 'Integrate with Xero Practice Manager'
              : 'Xero Practice Manager'}
          </Typography>

          <StatusDescription status={status} />

          {/* Button Group */}
          <Stack
            direction="row"
            sx={(theme) => ({
              gap: theme.semantic.spacing.lg,
            })}
          >
            {status === 'not-connected' && (
              <>
                <Button variant="contained" onClick={onConnect}>
                  CONNECT XPM
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleHowItWorks}
                  endIcon={<OpenInNewIcon />}
                >
                  HOW IT WORKS
                </Button>
              </>
            )}

            {status === 'access-lost' && (
              <AccessLostActions
                isReconnecting={isReconnecting}
                onReconnect={onReconnect}
                onHowItWorks={handleHowItWorks}
              />
            )}

            {status === 'connected' && (
              <>
                <Button
                  variant="contained"
                  onClick={handleHowItWorks}
                  endIcon={<OpenInNewIcon />}
                >
                  HOW IT WORKS
                </Button>
                <Button
                  variant="outlined"
                  onClick={onDisconnect}
                  sx={(theme) => ({
                    color: theme.palette.error.main,
                    borderColor: theme.palette.error.main,
                    borderRadius: theme.semantic.borderRadius.sm,
                    height: '44px', // Hardcoded: Figma 2.75rem
                    px: theme.semantic.spacing.base, // Token/base (16px)
                    py: theme.semantic.spacing.sm, // Spacing/spacing-sm (12px)
                    '&:hover': {
                      borderColor: theme.palette.error.dark,
                      backgroundColor: 'transparent',
                    },
                  })}
                >
                  DISCONNECT
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <Button variant="contained" onClick={onContactSupport}>
                  CONTACT SUPPORT
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleHowItWorks}
                  endIcon={<OpenInNewIcon />}
                >
                  HOW IT WORKS
                </Button>
                <Button
                  variant="outlined"
                  onClick={onDisconnect}
                  sx={(theme) => ({
                    color: theme.palette.error.main,
                    borderColor: theme.palette.error.main,
                    borderRadius: theme.semantic.borderRadius.sm,
                    height: '44px', // Hardcoded: Figma 2.75rem
                    px: theme.semantic.spacing.base, // Token/base (16px)
                    py: theme.semantic.spacing.sm, // Spacing/spacing-sm (12px)
                    '&:hover': {
                      borderColor: theme.palette.error.dark,
                      backgroundColor: 'transparent',
                    },
                  })}
                >
                  DISCONNECT
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>

      {/* Connected Timestamp (for every state except not-connected) */}
      {status !== 'not-connected' && connectedAt && (
        <Stack direction="row" justifyContent="flex-end">
          <Typography
            variant="body1"
            sx={(theme) => ({
              color: theme.palette.grey[500],
            })}
          >
            Connected {formatConnectedDate(connectedAt)}
          </Typography>
        </Stack>
      )}
    </Paper>
  );
};

export const XpmIntegrationWrapper = withClarityTheme(
  XpmIntegrationWrapperComponent,
);

export type { XpmIntegrationStatus, XpmIntegrationWrapperProps };
